// Default values for a new project. A field missing from a file takes its value from
// here, so these are also the values an older file is read with. GPL-3.0-or-later.
import { PROJECT_FORMAT, PROJECT_VERSION, UNITS, type Material, type Project, type SlopeSection, type StereoSettings, type TunnelSection, type DataSection, type ReportSection } from "./schema.js";

/** Strength defaults as in DipField Slope 0.1.0 (JCS 50 MPa = 50 000 kPa). */
export const material = (m: Partial<Material> = {}): Material => ({
  model: "mc", c: 0, phi: 30, jrc: 10, jcs: 50000, phir: 28, a: 1.5, b: 0.8, pc: 0, d: 0,
  waviness: 0, waterPressure: 0, spacing: 2, ...m,
});

export const defaultData = (): DataSection => ({
  format: "dip-dipdir", planes: [],
  traverses: [{ id: "T1", name: "T1", tr: { kind: "spot" } }],
  terzaghi: { use: false, minBiasDeg: 15 },
});

export const defaultStereo = (): StereoSettings => ({
  projection: { method: "equal-area", hemisphere: "lower" },
  density: { method: "fisher-overlap", countCircle: 0.01, coneRule: "dips" },
  display: { view: "net", poles: true, poleSize: 2.5, contours: true, contourStyle: "bands", overlay: "none",
    globalMean: false, bestFit: false, setPlanes: true, cones: false },
  rose: { mode: "strike", bin: 10, dipMin: 0, dipMax: 90, scale: "area", set: "all", showMean: true, ring: "auto", ringPct: 15, overlay: false },
  dse: { maxSets: 5, minAngleDeg: 30, coneDeg: 30, minPeakPct: 5, projection: "Equal-area", useWeights: true },
});

/** Slope defaults: DipField 1.0.0 kinematics (slope 60/180, φ 30°, ±20°) and the
 *  DipField Slope 0.1.0 limit-equilibrium defaults, except blocks from set means (2.3.0, D-022;
 *  with no joint sets the Suite uses every pole whatever this holds); toppling by the slope
 *  engine's contact rules, the only rules since 2.6.0 (D-025). */
export const defaultSlope = (): SlopeSection => ({
  face: { dip: 60, dipDir: 180 },
  lateral: 20,
  kinematics: { enabled: false, friction: 30, noLimits: false, show: "planar", pairs: "all", showZone: true, chart: "dipDir" },
  geometry: { upper: { dip: 0, dipDir: 180 }, height: 20, length: 50, unitWeight: 26, bench: null },
  materials: {},
  other: material(),
  run: { basis: "sets", pairs: "between", planar: true, wedge: true, topple: true, target: 1.3, toppleBase: null, toppleToe: 0, toppleMethod: "equal-area" },
  loads: { water: "none", fill: 50, seismicK: 0, seismicTrend: null, force: 0, forceTrend: 0, forcePlunge: 0 },
  caps: { volume: null, weight: null, trace: null, persistence: null },
  view: { tab: "net", intersections: true, listKind: "all" },
});

/** Tunnel defaults as in DipField 1.0.0 and the wedge3d app (horseshoe 10 × 10 m, trend 45°,
 *  unbounded, 27 kN/m³); design FS 1.5 and every wedge listed, as the wedge3d app. */
export const defaultTunnel = (): TunnelSection => ({
  shape: "horseshoe", width: 10, height: 10, section: null, sectionName: null, trend: 45, plunge: 0, length: null, unitWeight: 27,
  joints: {}, combo: null, target: 1.5, view: { tab: "3d", perimeter: true, nearEnd: true, farEnd: true, unstableOnly: false },
});

/** Report defaults: every part and item, except the rosette and the list of every pole. */
/** Report defaults: every part but Method & checks (2.7.0: it describes the software, not the
 *  analysis, and runs to several pages), every item but the rosette and the list of poles. */
export const defaultReport = (): ReportSection => ({
  title: "", author: "", notes: "",
  parts: { data: true, slope: true, tunnel: true, method: false },
  data: { net: true, rosette: false, sets: true, poles: false },
  slope: { inputs: true, kinematics: true, blocks: true, charts: true, sheets: true },
  tunnel: { inputs: true, combos: true, figures: true, wedges: true, modes: true },
});

let counter = 0;
/** A project id: random where the platform offers it, unique within the session otherwise. */
export function newId(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
  if (c?.randomUUID) return c.randomUUID();
  return `p-${Date.now().toString(36)}-${(counter++).toString(36)}`;
}

export function newProject(o: { name?: string; id?: string; now?: string } = {}): Project {
  const now = o.now ?? new Date().toISOString();
  return {
    format: PROJECT_FORMAT, version: PROJECT_VERSION,
    meta: { id: o.id ?? newId(), name: o.name ?? "Untitled project", createdAt: now, savedAt: now, savedBy: null, engines: {}, migratedFrom: null },
    units: { ...UNITS },
    data: defaultData(), sets: [], stereo: defaultStereo(), slope: defaultSlope(), tunnel: defaultTunnel(), report: defaultReport(),
  };
}
